<script lang="ts" setup>
import { computed } from 'vue';
import type { FormStructure } from '../../types/formTypeStructure';
import FormSection from '../FormSection.vue';
import FormPreviewSection from '../FormPreviewSection.vue';
import FormNavigation from '../FormNavigation.vue';
import FormProgressBar from '../FormProgressBar.vue';
import FormSyncComponent from './FormSyncComponent.vue';

interface Props {
  form: FormStructure;
  formId: string;
  currentLang: string;
  navigation: any; // Type sera défini plus tard
  formValues: Record<string, any>;
  handleFieldChange: (fieldName: string, value: any) => void;
  onClearAll: () => void;
  handleNext: () => Promise<void>;
  handlePrevious: () => Promise<void>;
  handleFinalSubmit: () => Promise<void>;
  isValidating: boolean;
}

const props = defineProps<Props>();

const debugInfo = computed(() => ({
  sectionIndex: props.navigation.sectionIndex.value,
  fieldPage: props.navigation.fieldPage.value,
  sectionCount: props.navigation.debugInfo?.value?.sectionCount || 0,
  totalFieldPages: props.navigation.debugInfo?.value?.totalFieldPages || 0,
  fieldsOnCurrentPage: props.navigation.debugInfo?.value?.fieldsOnCurrentPage || 0,
}));

// Options de layout (dont la barre de progression)
const progressBarType = computed(() => props.form.layoutOptions?.progressBarType);
const sections = computed(() => props.form.sections);
const currentSectionIndex = computed(() => props.navigation.sectionIndex.value);
</script>

<template>
  <form @submit.prevent>
    <!-- Barre de progression : entre le header et la section -->
    <FormProgressBar 
      :currentStep="navigation.currentStep.value" 
      :totalSteps="navigation.totalSteps.value"
      :progressBarType="progressBarType"
      :sections="sections"
      :currentSectionIndex="currentSectionIndex"
    />
    
    <!-- Section de prévisualisation -->
    <FormPreviewSection
      v-if="navigation.isPreviewSection.value"
      :form="form"
      :formValues="formValues"
      :currentLang="currentLang"
    />
    
    <!-- Section normale -->
    <FormSection
      v-else
      :key="`section_${navigation.fadeKey.value}_${navigation.currentSection.value?.sectionId}`"
      :section="navigation.currentSection.value"
      :fields="navigation.pagedFields.value"
      :currentLang="currentLang"
      :fadeKey="navigation.fadeKey.value"
      :formValues="formValues"
      :debugInfo="debugInfo"
      :allSections="form.sections"
      :onSubmit="handleFinalSubmit"
      :onNext="handleNext"
      :isLastSection="navigation.isLastSection.value"
      :onClearForm="onClearAll"
      @onFieldChange="handleFieldChange"
    />
    
    <FormNavigation
      :onPrevious="handlePrevious"
      :onNext="handleNext"
      :onClearAll="onClearAll"
      :onSubmit="handleFinalSubmit"
      :isFirstSection="navigation.isFirstSection.value"
      :isLastSection="navigation.isLastSection.value || navigation.isPreviewSection.value"
      :isValidating="isValidating"
    />

    <!-- Composant de synchronisation automatique -->
    <FormSyncComponent
      :formId="formId"
      :sections="form.sections"
      :formValues="formValues"
    />
  </form>
</template>

<style scoped>
form {
  @apply h-full w-full flex flex-col items-center justify-center;
}
</style>

